// Decides whether the card being typed is corporate, for the corporate/personal tabs.
//
// Two signals exist. Adyen's drop-in reports payment-method variants from its own
// BIN lookup once six digits are typed; a keyword check on those variants is what the
// tabs relied on until now. It misreports Mastercard business debit as plain "mcdebit"
// and comes back without variants for a large share of cards. payment-service's
// card-details lookup (Adyen's /cardDetails, possible once the full number is valid)
// agreed with the post-payment webhook on every payment measured in October 2026, so
// it decides. The drop-in verdict shows provisionally while the number is still being
// typed, and is the fallback when the lookup fails, times out or has no answer.

export const CORP_TOKENS = [
  "commercial",
  "corporate",
  "business",
  "purchasing",
  "fleet",
];

export const CARD_DETAILS_TIMEOUT_MS = 4000;

// After a paste over a complete number the drop-in reports a BIN change only if
// the BIN differs; this is how long a fallback waits for that report before
// concluding the BIN is unchanged and the drop-in verdict it has still applies.
export const BIN_CHANGE_GRACE_MS = 1000;

// Browsers reject a bare `setTimeout` called as a method of another object
// ("Illegal invocation"), so the globals are wrapped rather than referenced.
export const browserTimers = {
  setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimeout: (handle) => clearTimeout(handle),
};

export function hasCorporateVariant(variants) {
  return (
    Array.isArray(variants) &&
    variants.some((v) => CORP_TOKENS.some((tok) => v?.includes(tok)))
  );
}

/**
 * @param {object} options
 * @param {(isCorporate: boolean) => void} [options.onCardTypeChange]
 * @param {(pending: boolean) => void} [options.setBinLookupPending]
 * @param {(encryptedCardNumber: string) => Promise<{ isCorporate?: boolean | null } | null>} [options.lookup]
 *   Resolves with the card-details response, or null when the call failed. Omit it to
 *   decide from the drop-in variants alone.
 * @param {number} [options.timeoutMs]
 * @param {number} [options.binChangeGraceMs]
 * @param {{ setTimeout: Function, clearTimeout: Function }} [options.timers]
 */
export function createCardTypeResolver({
  onCardTypeChange,
  setBinLookupPending,
  lookup,
  timeoutMs = CARD_DETAILS_TIMEOUT_MS,
  binChangeGraceMs = BIN_CHANGE_GRACE_MS,
  timers = browserTimers,
}) {
  let dropinVerdict = null; // from the drop-in's BIN lookup, for the current BIN
  // Set when a complete number was replaced directly by another: the drop-in verdict
  // then belongs to the previous card until a BIN change is reported, or until the
  // grace passes with none, which means the BIN is the same.
  let dropinVerdictMayBeStale = false;
  let binChangeGraceHandle = null;
  let lookupVerdict = null; // from the card-details lookup, once it has answered
  let lookupOutstanding = false;
  let lookupSeq = 0;
  let lastBin = null;
  let lookedUpEncryptedCardNumber = null;

  const decide = (verdict) => {
    onCardTypeChange?.(verdict);
    setBinLookupPending?.(false);
  };

  const stopWaitingForBinChange = () => {
    if (binChangeGraceHandle === null) return;
    timers.clearTimeout(binChangeGraceHandle);
    binChangeGraceHandle = null;
  };

  const waitForBinChange = () => {
    try {
      binChangeGraceHandle = timers.setTimeout(() => {
        binChangeGraceHandle = null;
        dropinVerdictMayBeStale = false;
        decide(dropinVerdict);
      }, binChangeGraceMs);
    } catch {
      dropinVerdictMayBeStale = false;
      decide(dropinVerdict);
    }
  };

  const fallBackToDropin = () => {
    if (dropinVerdict === null) return; // the drop-in decides when it answers
    if (dropinVerdictMayBeStale) {
      waitForBinChange();
      return;
    }
    decide(dropinVerdict);
  };

  const settle = (result) => {
    lookupOutstanding = false;
    if (typeof result?.isCorporate === "boolean") {
      lookupVerdict = result.isCorporate;
      decide(lookupVerdict);
      return;
    }
    fallBackToDropin();
  };

  const issueLookup = (encryptedCardNumber) => {
    const seq = ++lookupSeq;
    lookupVerdict = null;
    lookupOutstanding = true;
    stopWaitingForBinChange();
    setBinLookupPending?.(true);

    // Whichever comes first settles: the answer, a failure, or the timeout. A
    // settle must always happen, or the Pay button stays disabled.
    let settled = false;
    let timeoutHandle = null;
    const settleOnce = (result) => {
      if (settled) return;
      settled = true;
      if (timeoutHandle !== null) timers.clearTimeout(timeoutHandle);
      if (seq !== lookupSeq) return; // superseded by a later card number
      settle(result);
    };

    try {
      timeoutHandle = timers.setTimeout(() => settleOnce(null), timeoutMs);
    } catch {
      timeoutHandle = null; // no timeout; the lookup's own outcome settles
    }

    let answered;
    try {
      answered = Promise.resolve(lookup(encryptedCardNumber));
    } catch {
      answered = Promise.resolve(null);
    }
    answered.then(settleOnce, () => settleOnce(null));
  };

  // The number this verdict was obtained for is gone: a new number gets its own
  // lookup, an answer still in flight for the old one must not land, and nothing
  // is pending any more.
  const discardLookup = () => {
    const wasDeciding = lookupOutstanding || binChangeGraceHandle !== null;
    lookupSeq++;
    lookupOutstanding = false;
    lookupVerdict = null;
    lookedUpEncryptedCardNumber = null;
    stopWaitingForBinChange();
    if (wasDeciding) {
      setBinLookupPending?.(false);
    }
  };

  return {
    // The first digits changed. The drop-in reports this and the new encrypted
    // number as separate events in an order this code cannot rely on, so a
    // lookup verdict is kept here: it belongs to the encrypted number it answered
    // and is cleared only when that number is replaced or becomes invalid.
    onBinValue(binValue) {
      if (typeof binValue !== "string" || binValue.length < 6) return;
      if (binValue === lastBin) return;
      lastBin = binValue;
      dropinVerdict = null;
      dropinVerdictMayBeStale = false;
      stopWaitingForBinChange();
      if (lookupVerdict === null) {
        setBinLookupPending?.(true);
      }
    },

    onBinLookup(variants) {
      dropinVerdict = hasCorporateVariant(variants);
      dropinVerdictMayBeStale = false;
      stopWaitingForBinChange();
      if (lookupVerdict === null) {
        onCardTypeChange?.(dropinVerdict);
      }
      if (!lookupOutstanding) {
        setBinLookupPending?.(false);
      }
    },

    // Called on every drop-in state change; the lookup runs once per valid card
    // number. The drop-in re-encrypts the number whenever it changes, so a new
    // blob means a new card, even when a paste over a complete number never
    // passes through an invalid state.
    onCardNumberState({ valid, encryptedCardNumber }) {
      if (!valid) {
        discardLookup();
        return;
      }
      if (!encryptedCardNumber || !lookup) return;
      if (encryptedCardNumber === lookedUpEncryptedCardNumber) return;
      if (lookedUpEncryptedCardNumber !== null) {
        dropinVerdictMayBeStale = true; // replaced directly: a paste over a complete number
      }
      lookedUpEncryptedCardNumber = encryptedCardNumber;
      issueLookup(encryptedCardNumber);
    },
  };
}
